import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { mangaList, categories } from '@/mocks/manga';

const categorySlugMap: Record<string, string> = {
  lianai: 'lianai-romance',
  dongzuo: 'dongzuo-action',
  qihuan: 'qihuan-fantasy',
  xuanyi: 'xuanyi-thriller',
  gaoxiao: 'gaoxiao-comedy',
  BL: 'bl-comics',
  GL: 'gl-comics',
  rexia: 'rexia-recommend',
};

function generateChapters(total: number) {
  const chapters = [];
  for (let i = total; i >= 1; i--) {
    chapters.push({
      number: i,
      title: `第${i}话`,
      updateDate: `2026-${String(Math.max(1, 8 - Math.floor((total - i) / 10))).padStart(2, '0')}-${String(Math.max(1, 30 - (total - i) % 30)).padStart(2, '0')}`,
    });
  }
  return chapters;
}

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find(m => m.id === Number(id));
  const category = manga ? categories.find(c => c.id === manga.category) : undefined;

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-400"></i>
            <p className="mt-4 text-xl text-foreground-700">漫画未找到</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = generateChapters(manga.chapters);
  const relatedManga = mangaList
    .filter(m => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '漫画', href: `/category/${categorySlugMap[manga.category] || 'rexia-recommend'}` },
                { label: manga.title },
              ]}
            />
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-64 flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={manga.cardImage}
                  alt={manga.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {manga.title}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">作者：{manga.author}</p>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  manga.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-800'
                }`}>
                  {manga.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-700">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{manga.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-eye-line mr-1"></i>
                  {(manga.views / 10000).toFixed(0)}万阅读
                </span>
                <span className="text-sm text-foreground-600">
                  共 <strong>{manga.chapters}</strong> 话
                </span>
                <span className="text-sm text-foreground-600">
                  更新于 {manga.updateDate}
                </span>
              </div>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {manga.description}
                </p>
              </div>

              <div className="flex items-center gap-3 mt-6 flex-wrap">
                <a
                  href={`#chapter-1`}
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  开始阅读
                </a>
                <a
                  href={`#chapter-${manga.chapters}`}
                  className="px-6 py-2.5 border border-primary-300 text-primary-500 hover:bg-primary-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  阅读最新话
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="chapter-list" className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">章节列表</h2>
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto">
              {chapters.map((chapter) => (
                <a
                  key={chapter.number}
                  id={`chapter-${chapter.number}`}
                  href="#"
                  className="flex items-center justify-between px-5 py-3.5 border-b border-background-100 last:border-0 hover:bg-primary-50 transition-colors duration-150"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">
                    {chapter.updateDate}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {relatedManga.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedManga.map((rm) => (
                <Link
                  key={rm.id}
                  to={`/article/${rm.id}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
                    <img
                      src={rm.cardImage}
                      alt={rm.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/60 backdrop-blur-sm rounded-full px-2 py-0.5">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      <span className="text-background-50 text-[10px] font-medium">{rm.rating}</span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {rm.title}
                    </h3>
                    <p className="text-xs text-foreground-600 mt-1">{rm.author}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}